import React, { useCallback, useEffect, useRef, useState } from 'react';
import Input from '../../DesignSystem/input';
import FormFieldBox from '../../DesignSystem/formFieldBox';
import { PrivateNodeFieldsMapping, ErrorMessages } from '../../../utils/appConstants';
import AuthService from '../../../services/auth.service';
import DashboardService from '../../../services/dashboard.service';
import AddPortService from '../../../services/addPort.service';
import Dropdown from '../../DesignSystem/dropdown';
import PrivateNode from '../../../pages/PrivateNode';


interface PrivateNodeFormProps {
  onFieldChange: (fieldName: string, value: string) => void;
  onEmailBlur: (_: any) => void;
  unameError: string;
  emailError: string;
  scrollToTop: boolean;
  onKeypress: (e: any) => void;
}

const PrivateNodeForm: React.FC<PrivateNodeFormProps> = ({
  onKeypress, onFieldChange, onEmailBlur, unameError, emailError, scrollToTop,
}) => {
  const ref = useRef(null);
  //const [username, setUserName] = useState("");
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    username: '',
    email: '',
    company: '',
    contact: '',
    address: '',
    country: '',
    state: '',
    city: '',
    zip: ''
  });
  const [selectedNodeIds, setSelectedNodeIds] = useState<number[]>([]);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [networkElements, setNetworkElements] = useState<any[]>([]);
  const [isPrivateNode, setIsPrivateNode] = useState(true);
  const [isFormEditable, setIsFormEditable] = useState(true);
  const [organizationMembers, setOrganizationMembers] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  const fetchNetworkElements = useCallback(() => {
    AddPortService.getNetworkElements()
      .then((response: any) => {
        setNetworkElements(response.data || []);
      })
      .catch(() => {
        setNetworkElements([]);
      })
  }, []);

  const fetchOrganizationMembers = useCallback(async () => {
    try {
      const response = await AuthService.getUserOrganization(); 
      console.log(response.data)
      setOrganizationMembers(response.data || []);
    } catch (error) {
      console.error('Error fetching organization members:', error);
      setOrganizationMembers([]);
    }
  }, []);
  
  useEffect(() => {
    fetchNetworkElements();
  }, [fetchNetworkElements]);

  useEffect(() => {
    DashboardService.getSelectedNodes()
      .then((response: any) => {
        setSelectedNodeIds(response.data.selected_node_ids || []);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    Promise.all([
      AuthService.getUserData(),
      fetchOrganizationMembers()
    ])
    .then(([userDataResponse]) => {
      const userData = userDataResponse.data;
      if (userData) {
        setSelectedContact(userData);
        setCurrentUser(userData);
        setFormData({
          fname: userData.firstName || '',
          lname: userData.lastName || '',
          username: userData.username || '',
          email: userData.email || '',
          company: userData.organizationName || '',
          contact: userData.contactNo || '',
          address: userData.address || '',
          country: userData.country || '',
          state: userData.state || '',
          city: userData.city || '',
          zip: userData.zipcode || ''
        });
        onFieldChange(PrivateNodeFieldsMapping.fname, userData.firstName || '');
        onFieldChange(PrivateNodeFieldsMapping.lname, userData.lastName || '');
        onFieldChange(PrivateNodeFieldsMapping.uname, userData.username || '');
        onFieldChange(PrivateNodeFieldsMapping.email, userData.email || '');
        onFieldChange(PrivateNodeFieldsMapping.company, userData.organizationName || '');
        onFieldChange(PrivateNodeFieldsMapping.contact, userData.contactNo || '');
        onFieldChange(PrivateNodeFieldsMapping.address, userData.address || '');
        onFieldChange(PrivateNodeFieldsMapping.country, userData.country || '');
        onFieldChange(PrivateNodeFieldsMapping.state, userData.state || '');
        onFieldChange(PrivateNodeFieldsMapping.city, userData.city || '');
        onFieldChange(PrivateNodeFieldsMapping.zip, userData.zipcode || '');
      }
    })
    .catch(error => {
      console.error('Error fetching data:', error);
    });
  }, [fetchOrganizationMembers]);

  const handleInputChange = useCallback((field: string, e: any) => {
    if (!isFormEditable) return;

    const allowSpace = [
      PrivateNodeFieldsMapping.country,
      PrivateNodeFieldsMapping.address, 
      PrivateNodeFieldsMapping.city,
    ];

    const value = allowSpace.includes(field) ? (e?.target?.value ?? '') : (e?.target?.value.trim() ?? '');
    
    setFormData(prevData => ({
      ...prevData,
      [field]: value
    }));
    onFieldChange(field, value);
  }, [onFieldChange, isFormEditable]);

  useEffect(() => {
    if (scrollToTop) {
      // @ts-ignore
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [scrollToTop, ref]);

  const handleNodeSelection = (value: string) => {
    if (value === 'private') {
      setIsPrivateNode(true);
      setSelectedNode(null);
      setIsFormEditable(true);
      setFormData(prev => ({
        ...prev,
        address: '',
        city: '',
        state: '',
        country: '',
        zip: ''
      }));
    } else {
      setIsPrivateNode(false);
      const selectedNode = networkElements.find(ne => ne.id === value);
      setSelectedNode(selectedNode);
      setIsFormEditable(false);
      if (selectedNode) {
        setFormData(prev => ({
          ...prev,
          address: selectedNode.street || '',
          city: selectedNode.city || '',
          state: selectedNode.state || '',
          country: selectedNode.country || '',
          zip: selectedNode.zip || ''
        }));
      }
    }
  };

  const handleContactSelection = (contactId: string) => {
    const selectedMember = organizationMembers.find(member => member.id === contactId);
    if (selectedMember) {
      setSelectedContact(selectedMember);

      setFormData({
        fname: selectedMember.firstName || '',
        lname: selectedMember.lastName || '',
        username: selectedMember.username || '',
        email: selectedMember.email || '',
        company: selectedMember.organizationName || '',
        contact: selectedMember.contactNo || '',
        address: selectedMember.address || '',
        country: selectedMember.country || '',
        state: selectedMember.state || '',
        city: selectedMember.city || '',
        zip: selectedMember.zipcode || ''
      });
      
      onFieldChange(PrivateNodeFieldsMapping.fname, selectedMember.firstName || '');
      onFieldChange(PrivateNodeFieldsMapping.lname, selectedMember.lastName || '');
      onFieldChange(PrivateNodeFieldsMapping.uname, selectedMember.username || '');
      onFieldChange(PrivateNodeFieldsMapping.email, selectedMember.email || '');
      onFieldChange(PrivateNodeFieldsMapping.company, selectedMember.organizationName || '');
      onFieldChange(PrivateNodeFieldsMapping.contact, selectedMember.contactNo || '');
      onFieldChange(PrivateNodeFieldsMapping.address, selectedMember.address || '');
      onFieldChange(PrivateNodeFieldsMapping.country, selectedMember.country || '');
      onFieldChange(PrivateNodeFieldsMapping.state, selectedMember.state || '');
      onFieldChange(PrivateNodeFieldsMapping.city, selectedMember.city || '');
      onFieldChange(PrivateNodeFieldsMapping.zip, selectedMember.zipcode || '');

    }
  };

  const dropdownOptions = [
    { id: 'private', text: 'Create a private node', icon: null },
    ...networkElements
      .filter(node => selectedNodeIds.includes(node.id))
      .map(node => ({
        id: node.id,
        text: node.ne_name,
        icon: null
      }))
  ];

  const contactDropdownOptions = organizationMembers.map(member => ({
    id: member.id,
    text: `${member.firstName} ${member.lastName}${member.id === currentUser?.id ? ' (You)' : ''}`,
    icon: null
  }));

  return (
    <form className="form" name="registration-form">
      {/*<div className="two-form-fields-div">
        <div className="one-form-field-div">
          <FormFieldBox>
            <label>
              First Name
              <span className="asterisk">*</span>
            </label>
            <Input variant="input" name={PrivateNodeFieldsMapping.fname} value={formData.fname} onChange={(e) => handleInputChange(PrivateNodeFieldsMapping.fname, e)} onKeypress={onKeypress} />
          </FormFieldBox>
        </div>
        <div className="one-form-field-div">
          <FormFieldBox>
            <label>
              Last Name
              <span className="asterisk">*</span>
            </label>
            <Input variant="input" name={PrivateNodeFieldsMapping.lname} value={formData.lname} onChange={(e) => handleInputChange(PrivateNodeFieldsMapping.lname, e)} onKeypress={onKeypress} />
          </FormFieldBox>
        </div>
      </div>
      <FormFieldBox>
        <label ref={ref}>
          Username
          <span className="asterisk">*</span>
        </label>
        <Input variant="input" name={PrivateNodeFieldsMapping.uname} value={formData.username} onChange={(e) => handleInputChange(PrivateNodeFieldsMapping.uname, e)} error={!!unameError} errorMessage={unameError || ''} onKeypress={onKeypress} />
      </FormFieldBox>
      <FormFieldBox>
        <label>
          Email
          <span className="asterisk">*</span>
        </label>
        <Input name={PrivateNodeFieldsMapping.email} variant="input" value={formData.email} onChange={(e) => handleInputChange(PrivateNodeFieldsMapping.email, e)} error={!!emailError} errorMessage={emailError || ''} onBlur={onEmailBlur} onKeypress={onKeypress} />
      </FormFieldBox>
      <FormFieldBox>
        <label>
          Organization or Company Name
        </label>
        <Input variant="input" name={PrivateNodeFieldsMapping.company} value={formData.company} onChange={(e) => handleInputChange(PrivateNodeFieldsMapping.company, e)} onKeypress={onKeypress} />
      </FormFieldBox>
      <FormFieldBox>
        <label>
          Contact No.
          <span className="asterisk">*</span>
        </label>
        <Input variant="input" name={PrivateNodeFieldsMapping.contact} value={formData.contact} onChange={(e) => handleInputChange(PrivateNodeFieldsMapping.contact, e)} onKeypress={onKeypress} />
      </FormFieldBox>*/}

      <FormFieldBox>
        <div className="port-type-div">
          <label>
            Point of Contact
            <span className="asterisk">*</span>
          </label>
        </div>
        <Dropdown
          value={selectedContact?.id || ""}
          options={contactDropdownOptions}
          valueKey="id"
          textKey="text"
          icon="icon"
          onChange={handleContactSelection}
        />
      </FormFieldBox>
      
      <FormFieldBox>
        <div className="port-type-div">
            <label>
              Node Selection
              <span className="asterisk">*</span>
            </label>
        </div>
        <Dropdown
          value={isPrivateNode ? 'private' : (selectedNode?.id || "")}
          options={dropdownOptions}
          valueKey="id"
          textKey="text"
          icon="icon"
          onChange={handleNodeSelection}
        />
      </FormFieldBox>
      
      <FormFieldBox>
        <label>
          Address
          <span className="asterisk">*</span>
        </label>
        <Input 
          variant="input" 
          name={PrivateNodeFieldsMapping.address} 
          value={formData.address} 
          onChange={(e) => handleInputChange(PrivateNodeFieldsMapping.address, e)} 
          onKeypress={onKeypress}
          disabled={!isFormEditable}
        />
      </FormFieldBox>
      <div className="two-form-fields-div">
        <div className="one-form-field-div">
          <FormFieldBox>
            <label>
              Country
              <span className="asterisk">*</span>
            </label>
            <Input 
              variant="input" 
              name={PrivateNodeFieldsMapping.country} 
              value={formData.country} 
              onChange={(e) => handleInputChange(PrivateNodeFieldsMapping.country, e)} 
              onKeypress={onKeypress}
              disabled={!isFormEditable}
            />
          </FormFieldBox>
        </div>
        <div className="one-form-field-div">
          <FormFieldBox>
            <label>
              State
              <span className="asterisk">*</span>
            </label>
            <Input 
              variant="input" 
              name={PrivateNodeFieldsMapping.state} 
              value={formData.state} 
              onChange={(e) => handleInputChange(PrivateNodeFieldsMapping.state, e)} 
              onKeypress={onKeypress}
              disabled={!isFormEditable}
            />
          </FormFieldBox>
        </div>
      </div>
      <div className="two-form-fields-div">
        <div className="one-form-field-div">
          <FormFieldBox>
            <label>
              City
              <span className="asterisk">*</span>
            </label>
            <Input 
              variant="input" 
              name={PrivateNodeFieldsMapping.city} 
              value={formData.city} 
              onChange={(e) => handleInputChange(PrivateNodeFieldsMapping.city, e)} 
              onKeypress={onKeypress}
              disabled={!isFormEditable}
            />
          </FormFieldBox>
        </div>
        <div className="one-form-field-div">
          <FormFieldBox>
            <label>
              Zip
              <span className="asterisk">*</span>
            </label>
            <Input 
              variant="input" 
              name={PrivateNodeFieldsMapping.zip} 
              value={formData.zip} 
              onChange={(e) => handleInputChange(PrivateNodeFieldsMapping.zip, e)} 
              onKeypress={onKeypress}
              disabled={!isFormEditable}
            />
          </FormFieldBox>
        </div>
      </div>
    </form>
  );
};

export default PrivateNodeForm;
